/**
 * BioGold trusted-by badge: "Read what Kiwi men say" + avatar circles.
 * Each circle shows the photo from, and opens, one Judge.me review carrying the badge's tag
 * (data-ph-avatar-tags, default imageUse): click scrolls to the page's reviews section and, if the Judge.me
 * widget has that review rendered, on to the card with a brief highlight. Pages without a reviews section
 * follow the badge link instead. The static avatar images stay as the fallback when nothing is tagged.
 * Also drives the star-rating eyebrow circles (.m-rating-eyebrow__avatar).
 * Reviews come from the same public endpoint Judge.me's own Reviews Grid block uses.
 */
(function () {
  if (window.__bgTrustedByReviews) return;
  window.__bgTrustedByReviews = true;

  var AVATAR_SEL = ".m-slide__trusted-by-avatar, .m-landing-hero__trusted-avatar, .m-rating-eyebrow__avatar";
  var cache = {};

  function decode(value) {
    var raw = String(value || "");
    if (raw.indexOf("&") === -1) return raw.trim();
    var doc = new DOMParser().parseFromString(raw, "text/html");
    return (doc.documentElement.textContent || "").trim();
  }

  function fetchTagged(tags, limit) {
    var key = tags.join(",") + "|" + limit;
    if (cache[key]) return cache[key];
    var shop = (window.Shopify && window.Shopify.shop) || "";
    if (!shop || typeof fetch !== "function") return Promise.resolve([]);
    var params = new URLSearchParams({
      shop_domain: shop,
      platform: "shopify",
      page: "1",
      per_page: String(limit),
      review_selection: "all",
    });
    tags.forEach(function (tag) {
      params.append("custom_tags[]", tag);
    });
    cache[key] = fetch("https://api.judge.me/widgets/reviews_grid_widget_data?" + params.toString(), {
      headers: { Accept: "application/json" },
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        return data && Array.isArray(data.reviews) ? data.reviews : [];
      })
      .catch(function () {
        return [];
      });
    return cache[key];
  }

  function headerOffset() {
    var styles = getComputedStyle(document.documentElement);
    return (
      (parseFloat(styles.getPropertyValue("--m-header-height")) || 0) +
      (parseFloat(styles.getPropertyValue("--m-announcement-height")) || 0) +
      16
    );
  }

  function scrollToEl(el) {
    var top = el.getBoundingClientRect().top + window.scrollY - headerOffset();
    window.scrollTo({ top: top, behavior: "smooth" });
  }

  function reviewsTarget(badge) {
    var href = badge.getAttribute("href") || "";
    var target = null;
    if (href.charAt(0) === "#" && href.length > 1) {
      try {
        target = document.querySelector(href);
      } catch (error) {}
    }
    return target || document.getElementById("reviews") || document.querySelector('[data-section-type="customer-reviews"]');
  }

  function jump(badge, review) {
    /* Badge links to another page (e.g. the homepage → /pages/all-reviews): follow it, carrying the tagged
       review in the hash so that page lands on it. Only in-page anchors scroll here. */
    var href = badge.getAttribute("href") || "";
    if (href && href.charAt(0) !== "#") {
      var base = href.split("#")[0];
      window.location.assign(review && review.uuid ? base + "#review-" + review.uuid : base + "#reviews");
      return;
    }
    if (typeof window.bgScrollToReviews === "function" && reviewsTarget(badge)) {
      window.bgScrollToReviews(review && review.uuid);
      return;
    }
    var target = reviewsTarget(badge);
    if (!target) {
      window.location.assign(badge.getAttribute("href") || "/pages/all-reviews");
      return;
    }
    scrollToEl(target);
    var uuid = review && review.uuid;
    if (!uuid) return;
    var tries = 0;
    var tick = function () {
      var card = target.querySelector('[data-review-id="' + uuid + '"]');
      if (card) {
        scrollToEl(card);
        card.classList.add("m-ph-review-flash");
        window.setTimeout(function () {
          card.classList.remove("m-ph-review-flash");
        }, 2400);
        return;
      }
      if (++tries < 20) window.setTimeout(tick, 250);
    };
    window.setTimeout(tick, 400);
  }

  /* Reveal once the (possibly swapped) photos have loaded, or after a short grace period regardless. */
  function reveal(badge, avatars) {
    var pending = avatars.filter(function (img) {
      return img.src && !img.complete;
    });
    var done = false;
    var show = function () {
      if (done) return;
      done = true;
      badge.classList.add("is-avatars-ready");
    };
    if (!pending.length) return show();
    var left = pending.length;
    pending.forEach(function (img) {
      var one = function () {
        if (--left <= 0) show();
      };
      img.addEventListener("load", one, { once: true });
      img.addEventListener("error", one, { once: true });
    });
    window.setTimeout(show, 1500);
  }

  function enhance(badge) {
    if (badge.__bgEnhanced) return;
    badge.__bgEnhanced = true;
    var avatars = Array.prototype.slice.call(badge.querySelectorAll(AVATAR_SEL));
    var tags = String(badge.getAttribute("data-ph-avatar-tags") || "")
      .split(",")
      .map(function (tag) {
        return tag.trim();
      })
      .filter(Boolean);
    if (!avatars.length || !tags.length) return reveal(badge, avatars);

    fetchTagged(tags, avatars.length).then(function (reviews) {
      if (!badge.isConnected) return;
      if (!reviews.length) return reveal(badge, avatars);
      avatars.forEach(function (avatar, index) {
        var review = reviews[index];
        if (!review) return;
        var name = decode(review.reviewer_name) || "a customer";
        var picture = Array.isArray(review.pictures_urls) && review.pictures_urls.length ? review.pictures_urls[0] : null;
        var pictureUrl = picture && typeof picture === "object" ? picture.small || picture.compact || picture.original || "" : picture || "";
        if (pictureUrl) {
          avatar.removeAttribute("srcset");
          avatar.removeAttribute("sizes");
          avatar.removeAttribute("loading");
          avatar.src = pictureUrl;
          avatar.alt = "Photo from " + name + "'s review";
        }
        avatar.classList.add("is-linked");
        avatar.setAttribute("role", "button");
        avatar.setAttribute("tabindex", "0");
        avatar.setAttribute("aria-label", "Read " + name + "'s review");
        var open = function (event) {
          event.preventDefault();
          event.stopPropagation();
          jump(badge, review);
        };
        avatar.addEventListener("click", open);
        avatar.addEventListener("keydown", function (event) {
          if (event.key === "Enter" || event.key === " ") open(event);
        });
      });
      reveal(badge, avatars);
    });
  }

  function init() {
    document.querySelectorAll("[data-ph-avatar-tags]").forEach(enhance);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
  document.addEventListener("shopify:section:load", init);
})();
